import { i18n, type Locale } from '@lingui/core';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import timezone from 'dayjs/plugin/timezone.js';
import updateLocale from 'dayjs/plugin/updateLocale.js';
import utc from 'dayjs/plugin/utc.js';
import {
    createRelativeTimeConfig,
    type RelativeTimeConfig,
} from './relative-time';

// List of supported locales
// https://github.com/iamkun/dayjs/tree/dev/src/locale
import 'dayjs/locale/fi.js';
import 'dayjs/locale/nb.js';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);

// Override default formats for locales
// https://day.js.org/docs/en/display/format
const localeToFormats: Record<Locale, object> = {
    fi: {
        LT: '[klo] H.m',
        ll: 'D.M.YYYY',
        lll: 'D.M. [klo] H.m',
    },
    nb: {
        LT: '[kl] HH:mm',
        LLL: 'D. MMMM [kl]. HH:mm',
    },
};

const localeToRelativeTime: Record<Locale, RelativeTimeConfig> = {
    fi: createRelativeTimeConfig((input, withoutSuffix, token, isFuture) => {
        const past = {
            s: 'muutama sekunti',
            m: 'minuutti',
            mm: '%d minuuttia',
            h: 'tunti',
            hh: '%d tuntia',
            d: 'päivä',
            dd: '%d päivää',
            M: 'kuukausi',
            MM: '%d kuukautta',
            y: 'vuosi',
            yy: '%d vuotta',
        };
        const future = {
            s: 'muutaman sekunnin',
            m: 'minuutin',
            mm: '%d minuutin',
            h: 'tunnin',
            hh: '%d tunnin',
            d: 'päivän',
            dd: '%d päivän',
            M: 'kuukauden',
            MM: '%d kuukauden',
            y: 'vuoden',
            yy: '%d vuoden',
        };
        const words = isFuture && !withoutSuffix ? future : past;
        const result = words[token];
        return result.replace('%d', input.toString());
    }),
    nb: createRelativeTimeConfig((input, _, token) => {
        const numbers =
            'null_en_to_tre_fire_fem_seks_syv_åtte_ni_ti_elleve_tolv'.split(
                '_',
            );
        const words = {
            s: 'noen sekunder',
            m: 'ett minutt',
            mm: '%d minutter',
            h: 'en time',
            hh: '%d timer',
            d: 'en dag',
            dd: '%d dager',
            M: 'en måned',
            MM: '%d måneder',
            y: 'ett år',
            yy: '%d år',
        };
        const result = words[token];
        if (input < 13) {
            return result.replace('%d', numbers[input]);
        }
        return result.replace('%d', input.toString());
    }),
};

const localeToTimezone: Record<Locale, string> = {
    fi: 'Europe/Helsinki',
    nb: 'Europe/Oslo',
};

function loadActiveLocale() {
    // Wrap in a function to avoid the i18n.locale and i18n.t calls being
    // executed at import time. The locale is not yet set at that time.

    // Get config for active locale
    const localeConfig = dayjs.updateLocale(i18n.locale, {});

    // Customize config for active locale
    // https://day.js.org/docs/en/customization/customization
    dayjs.updateLocale(i18n.locale, {
        ...localeConfig,
        // Customize relative time
        // https://day.js.org/docs/en/customization/relative-time
        relativeTime: {
            ...(localeConfig.relativeTime as object),
            ...localeToRelativeTime[i18n.locale],
        },
        formats: {
            ...(localeConfig.formats as object),
            ...localeToFormats[i18n.locale],
        },
    });

    // Load active locale
    dayjs.locale(i18n.locale);

    // Set timezone for active locate
    dayjs.tz.setDefault(localeToTimezone[i18n.locale]);
}

// Create dayjs instance with timezone support
// https://github.com/iamkun/dayjs/issues/1227#issuecomment-952708863
const dayjsTz = dayjs.tz;

export function formatDateAndTime(date: number | Date): string {
    loadActiveLocale();
    return dayjsTz(date).format('lll');
}

export function formatTime(date: number | Date): string {
    loadActiveLocale();
    return dayjsTz(date).format('LT');
}

export function formatRelativeDate(
    date: number | Date,
    {
        currentTime,
        newTodayText = false,
    }: { currentTime?: number; newTodayText?: boolean } = {},
): string {
    loadActiveLocale();
    const dayDifference = dayjsTz(currentTime).diff(date, 'day');

    if (newTodayText && dayDifference < 1) {
        return i18n.t({
            id: 'date.new-today',
            message: 'New today',
        });
    } else if (dayDifference < 3) {
        return dayjsTz(date).from(currentTime);
    }

    return dayjsTz(date).format('ll');
}
